/**
 * Item List Page V2 — Playground for redesigning the item list table.
 * Route: /item-page2
 *
 * This is a copy of ItemListPage for experimentation.
 * Once the design is finalized, merge changes back into ItemListPage.
 */

import { useMemo, useCallback, useState, useEffect, useRef, type ReactNode } from "react";

import type { FilterDescriptor } from "@/lib/erp";
import { useAllBundleItemCodes } from "./api/useBundleItemCodes";
import { BundleBadge } from "./item-detail/components/BundleBadge";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFilterStore } from "@/lib/store/filterStore";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { useItemListWithVariants } from "./api/inventoryHooks";
import type { Item, ItemWithVariants } from "@/lib/erp";
import { usePageTranslation } from "@/i18n/useTranslation";
import { useLanguage } from "@/i18n/useLanguage";
import { inventoryTranslations } from "@/i18n/locales/en/inventory";
import { inventoryTranslations as thInventoryTranslations } from "@/i18n/locales/th/inventory";
import { getListViewSchema } from "@/lib/views/listViewSchemas";
import type { ListSettingsFieldConfig } from "@/lib/hooks/listSettingsTypes";
import {
  getActiveListViewConfig,
  buildColumnsFromListViewConfig,
  convertListViewSortToTanStackSort,
} from "@/lib/views/utils/buildListColumnsFromSchema";
import { StorefrontToggleCell } from "./components/StorefrontToggleCell";
import { StatusToggleCell } from "./components/StatusToggleCell";
import { Heart, MessageCircle, Clock, Users, ArrowRight, TableProperties, MoreHorizontal, Archive, ArrowUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useArchivedCount } from "@/features/common/api/useArchivedCount";
import type { SystemColumnToggle } from "@/features/common/components/ListSettingsModal";
import { InlineDetailPanel } from "./components/inline-detail/InlineDetailPanel";
import { GenericDetailPanel } from "@/features/list/GenericDetailPanel";
import { useDetailPanelState } from "@/features/list/useDetailPanelState";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { buildDetailRoute } from "@/lib/config/docRoutes";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";
import { useDebounce } from "@/hooks/useDebounce";
import { type SimpleTableColumn } from "@/features/common/components/SimpleTableView";
import { ProductListView } from "./components/ProductListView";
import { SimpleStorefrontCell } from "./components/SimpleStorefrontCell";
import { SimpleAhaProductView } from "./components/SimpleAhaProductView";
import { useAuthStore } from "@/lib/store/authStore";





type SortOption = "modified desc" | "item_name asc" | "standard_rate desc" | "standard_rate asc" | "creation desc";
const SORT_LABELS: Record<SortOption, string> = {
  "modified desc": "Recently updated",
  "item_name asc": "Name (A-Z)",
  "standard_rate desc": "Price (high to low)",
  "standard_rate asc": "Price (low to high)",
  "creation desc": "Newest first",
};
const SORT_KEY = "zvg-item-sort";

function getStoredSort(): SortOption {
  const stored = localStorage.getItem(SORT_KEY);
  if (stored && stored in SORT_LABELS) return stored as SortOption;
  return "modified desc";
}

const SYSCOL_STORAGE_KEY = "zvg-hidden-system-columns";

function readHiddenSystemCols(doctype: string): string[] {
  // Items have no assignees, so the "assigned to" avatar column is always empty
  // noise — hide it by default. Users can re-enable it via list settings (which
  // writes an explicit preference that overrides this default).
  const DEFAULT_HIDDEN = ["__assigned_to"];
  try {
    const raw = localStorage.getItem(SYSCOL_STORAGE_KEY);
    if (!raw) return DEFAULT_HIDDEN;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.[doctype]) ? parsed[doctype] : DEFAULT_HIDDEN;
  } catch { return DEFAULT_HIDDEN; }
}

function writeHiddenSystemCols(doctype: string, ids: string[]) {
  try {
    const raw = localStorage.getItem(SYSCOL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[doctype] = ids;
    localStorage.setItem(SYSCOL_STORAGE_KEY, JSON.stringify(parsed));
  } catch { /* ignore */ }
}

/**
 * Factory that returns a hook wrapping useItemListWithVariants with an
 * archive filter. When showArchived=false, prepends disabled=0 to
 * filterDescriptors so archived items are excluded.
 */
function createItemListHookWithArchive(showArchived: boolean) {
  return function useItemListHookWithArchive(params: Parameters<typeof useItemListWithVariants>[0]) {
    const archiveDescriptors: FilterDescriptor[] = showArchived
      ? []
      : [{ field: "disabled", operator: "equals" as const, value: "0" }];
    return useItemListWithVariants({
      ...params,
      filterDescriptors: [...archiveDescriptors, ...(params.filterDescriptors || [])],
    });
  };
}

export function ItemListPageV2() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { detailPanelItem, panelItem, openDetailPanel, closeDetailPanel, setDetailPanelItem } = useDetailPanelState();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl);

  // Mobile sort & search state (ProductListView has its own UI for these)
  const [listSort, setListSort] = useState<SortOption>(getStoredSort);
  const handleSortChange = useCallback((sort: SortOption) => {
    setListSort(sort);
    localStorage.setItem(SORT_KEY, sort);
  }, []);
  const [listSearchQuery, setListSearchQuery] = useState("");
  const debouncedSearch = useDebounce(listSearchQuery, 300);

  // On mobile, redirect ?detail= URL to full-page route
  useEffect(() => {
    if (isDesktop) return;
    if (detailPanelItem?.name) {
      setDetailPanelItem(null);
      navigate(buildDetailRoute("Item", detailPanelItem.name), { replace: true });
    }
  }, [isDesktop, detailPanelItem, navigate, setDetailPanelItem]);

  // On mobile, navigate to full-page detail route instead of opening panel
  const handleOpenDetail = useCallback((name: string, label?: string) => {
    if (isDesktop) {
      openDetailPanel(name, label);
    } else {
      navigate(buildDetailRoute("Item", name));
    }
  }, [isDesktop, openDetailPanel, navigate]);

  // Archive toggle state — URL param ?archived=true is the source of truth
  const [searchParamsArchive, setSearchParamsArchive] = useSearchParams();
  const showArchived = searchParamsArchive.get("archived") === "true";
  const { data: archivedCountData } = useArchivedCount("Item");
  const archivedCount = archivedCountData?.count ?? 0;

  const handleToggleArchived = useCallback(() => {
    setSearchParamsArchive((prev) => {
      const next = new URLSearchParams(prev);
      if (showArchived) {
        next.delete("archived");
      } else {
        next.set("archived", "true");
      }
      return next;
    });
  }, [showArchived, setSearchParamsArchive]);

  const useListHook = useMemo(
    () => createItemListHookWithArchive(showArchived),
    [showArchived],
  );

  // --- Bundle badge enrichment (fetch all active bundles once) ---
  const { bundleItemCodes } = useAllBundleItemCodes();
  // Ref so TanStack Table cell closures always read the latest value
  const bundleItemCodesRef = useRef(bundleItemCodes);
  bundleItemCodesRef.current = bundleItemCodes;

  // --- Inline detail panel state (row click = quick-peek) ---
  const [detailPanelItemCode, setDetailPanelItemCode] = useState<string | null>(null);
  const handlePanelClose = useCallback(() => setDetailPanelItemCode(null), []);

  // Auto-close panel when URL params change (filter/sort/pagination/search)
  const [searchParams] = useSearchParams();
  // G11: Exclude `detail` and `archived` params so toggling these doesn't auto-close inline panel
  const searchParamsKey = useMemo(() => {
    const filtered = new URLSearchParams(searchParams);
    filtered.delete("detail");
    filtered.delete("archived");
    return filtered.toString();
  }, [searchParams]);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    if (detailPanelItemCode) setDetailPanelItemCode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  // Auto-close panel when filter store changes
  const filters = useFilterStore(
    useCallback((s) => s.scopedFilters["inventory.items.v2"], []),
  );
  useEffect(() => {
    if (!mountedRef.current) return;
    if (detailPanelItemCode) setDetailPanelItemCode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Focus management: restore focus to row on panel close
  const lastOpenedItemRef = useRef<string | null>(null);
  useEffect(() => {
    if (detailPanelItemCode) {
      lastOpenedItemRef.current = detailPanelItemCode;
    } else if (lastOpenedItemRef.current) {
      const rowEl = document.querySelector(
        `[data-row-id="${CSS.escape(lastOpenedItemRef.current)}"]`,
      ) as HTMLElement | null;
      rowEl?.focus();
      lastOpenedItemRef.current = null;
    }
  }, [detailPanelItemCode]);



  // Row click removed — inline detail panel is opened via the table icon button in item_name column.
  // Double-click on cells enters edit mode.

  const renderSubRow = useCallback(
    (row: ItemWithVariants, columns: SubRowColumnInfo): ReactNode | null => {
      const itemCode = row.item_code ?? row.name;
      if (itemCode !== detailPanelItemCode) return null;
      return (
        <InlineDetailPanel
          key={itemCode}
          itemCode={itemCode}
          itemName={row.item_name}
          detailPath={`/inventory/item/${row.name}`}
          onClose={handlePanelClose}
          columns={columns}
        />
      );
    },
    [detailPanelItemCode, handlePanelClose],
  );

  const t = usePageTranslation({
    en: inventoryTranslations,
    th: thInventoryTranslations,
  });







  const [hiddenSystemCols, setHiddenSystemCols] = useState(() => readHiddenSystemCols("Item"));

  const systemColumnsConfig = useMemo(() => ({
    enabled: true,
    showAssignedTo: !hiddenSystemCols.includes("__assigned_to"),
    showCreated: !hiddenSystemCols.includes("__created"),
    showComments: !hiddenSystemCols.includes("__comments"),
    showLikes: !hiddenSystemCols.includes("__like"),
    assignedToField: "_assign",
    commentCountField: "comment_count",
    likedByField: "_liked_by",
    createdField: "creation",
  }), [hiddenSystemCols]);

  const systemColumnToggles = useMemo<SystemColumnToggle[]>(() => [
    { id: "__like", label: "Likes", icon: <Heart className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__like") },
    { id: "__comments", label: "Comments", icon: <MessageCircle className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__comments") },
    { id: "__created", label: "Created", icon: <Clock className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__created") },
    { id: "__assigned_to", label: "Assigned To", icon: <Users className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__assigned_to") },
  ], [hiddenSystemCols]);

  const handleSystemColumnToggle = useCallback((id: string, visible: boolean) => {
    setHiddenSystemCols((prev) => {
      const next = visible ? prev.filter((c) => c !== id) : [...prev, id];
      writeHiddenSystemCols("Item", next);
      return next;
    });
  }, []);

  const schema = getListViewSchema("Item");
  const viewConfig = getActiveListViewConfig(schema, "experience");

  const buildColumns = useMemo(
    () => {
      if (!viewConfig) {
        return () => [];
      }
      const schemaColumns = buildColumnsFromListViewConfig<ItemWithVariants>(viewConfig);
      return ({
        items: _items,
        visibleFieldOrder,
        availableFields
      }: {
        items: Item[] | ItemWithVariants[];
        visibleFieldOrder?: string[];
        availableFields?: ListSettingsFieldConfig[];
      }) => {
        void visibleFieldOrder;
        void availableFields;

        return schemaColumns.map((col) => {
          if (col.id === "item_name") {
            return {
              ...col,
              cell: ({ row, getValue }: { row: { original: ItemWithVariants }; getValue: () => unknown }) => {
                const value = getValue() as string | null;
                const itemCode = row.original.item_code ?? row.original.name;
                return (
                  <div className="relative flex items-center w-full min-w-0 group/name">
                    <span className="truncate">{value || row.original.name}</span>
                    {bundleItemCodesRef.current.has(itemCode) && (
                      <BundleBadge size="sm" className="ml-1.5 shrink-0" />
                    )}
                    <div className="absolute right-0 flex items-center gap-1">
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailPanelItemCode((prev) => prev === itemCode ? null : itemCode);
                        }}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium transition-colors border text-muted-foreground/70 hover:text-foreground bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 border-neutral-200 dark:border-neutral-700"
                        aria-label="Toggle inline details"
                      >
                        <TableProperties className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDetail(row.original.name, row.original.item_name ?? row.original.name);
                        }}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium text-muted-foreground/70 hover:text-foreground bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700 transition-colors"
                        aria-label="View item details"
                      >
                        Open
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                );
              },
            };
          }
          if (col.id === "storefront_enabled") {
            return {
              ...col,
              cell: ({ row }: { row: { getValue: (key: string) => unknown; original: ItemWithVariants } }) => {
                const value = row.getValue("storefront_enabled");
                const checked = value === 1 || value === true || value === "1";
                return (
                  <div className="hidden md:block text-center">
                    <StorefrontToggleCell
                      itemName={row.original.name}
                      storefrontEnabled={checked}
                    />
                  </div>
                );
              },
            };
          }
          if (col.id === "disabled") {
            return {
              ...col,
              cell: ({ row }: { row: { original: ItemWithVariants } }) => {
                const disabled = (row.original as Record<string, unknown>).disabled;
                const isActive = disabled === 0 || disabled === false || disabled === "0" || !disabled;
                return (
                  <StatusToggleCell
                    itemName={row.original.name}
                    isActive={isActive}
                    language={language}
                  />
                );
              },
            };
          }
          return col;
        });
      };
    },
    [viewConfig]
  );

  const defaultSorting = useMemo(() => {
    if (!viewConfig?.defaultSort) {
      return [{ id: "modified", desc: true }];
    }
    return convertListViewSortToTanStackSort(viewConfig.defaultSort);
  }, [viewConfig]);




  // --- Mobile product list data (only fetched on mobile) ---
  const mobileArchiveDescriptors: FilterDescriptor[] = showArchived
    ? []
    : [{ field: "disabled", operator: "equals" as const, value: "0" }];
  const listViewQuery = useItemListWithVariants({
    searchQuery: debouncedSearch,
    pageSize: 100,
    orderBy: listSort,
    filterDescriptors: mobileArchiveDescriptors,
  });
  const listItems = listViewQuery.data?.data ?? [];
  const listTotal = listViewQuery.data?.total;


  // --- Mobile header actions (sort + archive in ... menu) ---
  const mobileHeaderActions = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="shrink-0 h-8 w-8">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {(Object.entries(SORT_LABELS) as [SortOption, string][]).map(([key, label]) => (
          <DropdownMenuItem key={key} onClick={() => handleSortChange(key)}>
            <ArrowUpDown className="w-4 h-4 mr-2" />
            {label}
            {listSort === key && <span className="ml-auto text-blue-600">\u2713</span>}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleToggleArchived}>
          <Archive className="w-4 h-4 mr-2" />
          {showArchived ? "Hide archived" : "Show archived"}
          {archivedCount > 0 && (
            <span className="ml-1 text-muted-foreground">({archivedCount})</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Simple table columns (Shopify-style)
  const simpleColumns = useMemo<SimpleTableColumn<ItemWithVariants>[]>(() => [
    {
      id: "item_name",
      header: language === "th" ? "สินค้า" : "Product",
      accessor: (row) => row.item_name || row.name,
      cell: (row, value) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{String(value)}</span>
            {bundleItemCodesRef.current.has(row.item_code ?? row.name) && (
              <BundleBadge size="sm" className="shrink-0" />
            )}
          </div>
        </div>
      ),
    },
    {
      id: "item_code",
      header: "SKU",
      accessor: (row) => row.item_code ?? row.name,
      cell: (_row, value) => (
        <span className="font-mono text-[12px] text-neutral-500 dark:text-neutral-400 truncate block">
          {value != null && value !== "" ? String(value) : <span className="text-neutral-300 dark:text-neutral-700">—</span>}
        </span>
      ),
      hideBelow: "lg",
      width: "130px",
    },
    {
      id: "standard_rate",
      header: language === "th" ? "ราคา" : "Price",
      accessor: (row) => row.standard_rate,
      cell: (_row, value) => {
        const price = Number(value);
        if (!price && price !== 0) return <span className="text-neutral-300 dark:text-neutral-700">—</span>;
        return <span className="tabular-nums font-medium text-neutral-700 dark:text-neutral-300">{"฿"}{price.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span>;
      },
      align: "right",
      width: "80px",
    },
    {
      id: "status",
      header: language === "th" ? "สถานะ" : "Status",
      accessor: (row) => row.storefront_enabled,
      cell: (row) => (
        <SimpleStorefrontCell
          itemName={row.name}
          storefrontEnabled={(row.storefront_enabled as unknown) === 1 || (row.storefront_enabled as unknown) === true || (row.storefront_enabled as unknown) === "1"}
          hasImage={!!(row.primary_image || row.image)}
          hasPrice={!!(row.standard_rate && Number(row.standard_rate) > 0)}
          storefrontUrl={storefrontUrl ?? null}
          productSlug={(row.storefront_slug as string) || row.name}
          productName={String(row.item_name || row.name)}
          language={language}
          variantOf={(row.variant_of as string) || null}
        />
      ),
      width: "250px",
    },

  ], [language, storefrontUrl]);

  // Mobile: render ProductListView directly (your original mobile-only experience)
  if (!isDesktop) {
    return (
      <div key={language} className="min-h-screen bg-white dark:bg-neutral-950">
        <ProductListView
          items={listItems}
          bundleItemCodes={bundleItemCodes}
          onItemClick={handleOpenDetail}
          searchQuery={listSearchQuery}
          onSearchChange={setListSearchQuery}
          isLoading={listViewQuery.isLoading}
          totalCount={listTotal}
          headerActions={mobileHeaderActions}
          onNewClick={() => navigate("/inventory/item/new")}
        />
      </div>
    );
  }

  // Desktop: shell with Simple / List / Board tabs
  return (
    <div key={language} className="min-h-screen bg-white dark:bg-neutral-950 -mt-4 lg:mt-0">
      <div>
        <DocTypeListPageShell<ItemWithVariants>
          doctype="Item"
          showArchived={showArchived}
          inlineEditable
          isInlineEditMode
          columns={buildColumns}
          filterStoreKey="inventory.items.v2"
          useListHook={useListHook}
          defaultSorting={defaultSorting}
          pageSizeOptions={viewConfig?.pageSizeOptions ?? [20, 50, 100, 200, 400, 500]}
          searchPlaceholder={t.searchPlaceholder}
          title={t.items}
          newButtonLabel={t.newItem}
          translations={t}
          onNewClick={() => navigate("/inventory/item/new")}
          onRowClick={undefined}
          renderSubRow={renderSubRow}
          activeSubRowId={detailPanelItemCode ?? undefined}
          getRowId={(row) => row.name}
          getSubRows={(row) => (row as ItemWithVariants).variants}
          defaultExpanded={(row) => !!(row as ItemWithVariants).has_variants}
          thumbnailConfig={{
            imageFieldnames: ["primary_image", "image"],
            labelFieldnames: ["item_name", "item_code", "name"],
            sizePx: 28,
          }}
          compactMode
          enableSimpleView
          enableViewModeSwitch
          renderSimpleView={({ items: simpleItems, isLoading: simpleLoading, total: simpleTotal }) =>
            isDesktop ? (
              <SimpleAhaProductView
                items={simpleItems}
                columns={simpleColumns}
                isLoading={simpleLoading}
                total={simpleTotal}
                language={language}
                filterStoreKey="inventory.items.v2"
                subRowLabel={language === "th" ? "ตัวเลือก" : "variants"}
                onRowClick={(id, label) => handleOpenDetail(id, label)}
                onAddProduct={() => navigate("/inventory/item/new")}
              />
            ) : (
              <ProductListView
                items={simpleItems}
                bundleItemCodes={bundleItemCodes}
                onItemClick={handleOpenDetail}
                searchQuery=""
                onSearchChange={() => {}}
                isLoading={simpleLoading}
                totalCount={simpleTotal}
                onNewClick={() => navigate("/inventory/item/new")}
              />
            )
          }
          headerActions={() => (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="shrink-0 h-8 w-8">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleToggleArchived}>
                  <Archive className="w-4 h-4 mr-2" />
                  {showArchived ? "Hide archived" : "Show archived"}
                  {archivedCount > 0 && (
                    <span className="ml-1 text-muted-foreground">({archivedCount})</span>
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          breadcrumbs={[
            { label: t.items || "Items" },
          ]}
          galleryConfig={{
            sizePx: 160,
            titleFieldnames: ["item_name", "item_code"],
            subtitleFieldnames: ["item_group"],
          }}
          className="pt-0"
          fullWidth
          noPadding
          tableContainerClassName="overflow-x-auto overflow-y-hidden border-y border-border bg-white dark:bg-neutral-950"
          listSettingsConfig={{
            key: "inventory.items.v2",
            meta: {
              doctype: "Item",
              includeFieldtypes: [
                "Data",
                "Int",
                "Float",
                "Currency",
                "Check",
                "Date",
                "Datetime",
                "Link",
                "Select",
                "Small Text",
                "Text",
                "Long Text",
              ],
              excludeFieldtypes: [
                "Section Break",
                "Column Break",
                "HTML",
                "Table",
                "Table MultiSelect",
                "Button",
                "Heading",
              ],
              excludeFieldnames: [
                "naming_series",
                "owner",
                "creation",
                "modified",
                "modified_by",
                "idx",
                "__islocal",
                "__unsaved",
                "docstatus",
                "amended_from",
                "amended_date",
                "_assign",
                "_comment_count",
                "_liked_by",
              ],
              respectInListView: true,
              requiredFieldnames: ["item_name"],
              defaultVisibleFieldnames:
                viewConfig?.columns
                  ?.map((col) => col.fieldname)
                  .filter((v): v is string => typeof v === "string" && v.length > 0) || [],
              strictMode: false,
            },
            staticFields: [
              {
                fieldname: "__thumbnail",
                label: "Thumbnails",
                defaultVisible: true,
              },
            ],
          }}
          systemColumnsConfig={systemColumnsConfig}
          systemColumnToggles={systemColumnToggles}
          onSystemColumnToggle={handleSystemColumnToggle}
        />
      </div>

      {/* Panel only renders on desktop — mobile navigates to full-page route */}
      {isDesktop && (
        <GenericDetailPanel
          open={!!detailPanelItem}
          onClose={closeDetailPanel}
          doctype="Item"
          docName={panelItem?.name ?? ""}
          docLabel={panelItem?.label}
          wide
        />
      )}
    </div>
  );
}
